import { useCallback, useEffect, useState } from "react";

/**
 * One page image, loaded once and measured — for every view that draws on it
 * (plan 4 §5): the Original pane, its overlay and every excerpt share the
 * page's own pixel size, because their coordinates are fractions of exactly
 * that image.
 *
 * Measured from the file itself. Where a v2 reading says its page size
 * (`ReadingReviewOut.pageWidthPx/HeightPx`, plan 4 §9.1), the review draws in
 * that frame and uses this only for whether the image is in (`pageFrame`);
 * the measured size is the fallback. Until the image is in, nothing that
 * depends on its proportion is drawn — a guessed aspect would put every
 * outline beside its wall.
 */
export interface PageImage {
  src: string;
  status: "loading" | "loaded" | "failed";
  width: number;
  height: number;
  /** Ask again — the page route answered with an error, or the network did. */
  retry: () => void;
}

interface Loaded {
  key: string;
  status: "loaded" | "failed";
  width: number;
  height: number;
}

export function usePageImage(url: string): PageImage {
  const [attempt, setAttempt] = useState(0);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  // A retry asks the server again rather than the browser's memory of the
  // failure; a data URL (the dev preview's sketch) takes no query.
  const src = attempt === 0 || url.startsWith("data:") ? url : `${url}${url.includes("?") ? "&" : "?"}r=${attempt}`;

  useEffect(() => {
    if (url === "") return;
    let current = true;
    const image = new Image();
    image.onload = () => {
      if (!current) return;
      setLoaded({ key: src, status: "loaded", width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      if (current) setLoaded({ key: src, status: "failed", width: 0, height: 0 });
    };
    image.src = src;
    return () => {
      current = false;
    };
  }, [src, url]);

  const retry = useCallback(() => setAttempt((count) => count + 1), []);
  const mine = loaded !== null && loaded.key === src ? loaded : null;
  // A page whose file has no size of its own (an SVG without one) is not a
  // page that can carry an overlay.
  const usable = mine !== null && mine.status === "loaded" && mine.width > 0 && mine.height > 0;
  return {
    src,
    status: url === "" ? "failed" : mine === null ? "loading" : usable ? "loaded" : "failed",
    width: usable ? mine.width : 0,
    height: usable ? mine.height : 0,
    retry,
  };
}

/**
 * The frame every overlay on a reading's page is drawn in: the page size the
 * reading names (one truth with the worker's coordinates), else the image's
 * own. Fractions of this box are fractions of the page whatever the served
 * file measures, as long as it is drawn into the same box.
 */
export function pageFrame(
  page: { pageWidthPx?: number; pageHeightPx?: number } | null,
  image: Pick<PageImage, "width" | "height">,
): { width: number; height: number } {
  const width = page?.pageWidthPx ?? 0;
  const height = page?.pageHeightPx ?? 0;
  return width > 0 && height > 0 ? { width, height } : { width: image.width, height: image.height };
}
